import { createAction, createSlice } from "@reduxjs/toolkit";
// Types
import { Document } from "../../@types/document";
import { DocumentStakeholder } from "../../@types/documentStakeholder";
import { Message } from "../../@types/message";
import { addDocumentHistory } from "../documentUpdate/actions/addDocumentHistory";
import { moveStakeholderUp } from "../documentUpdate/actions/moveStakeholderUp";
import { moveStakeholderDown } from "../documentUpdate/actions/moveStakeholderDown";
import updateDocument from "../documentUpdate/actions/updateDocument";
import addStakeholder from "./actions/addStakeholder";
import deleteStakeholder from "./actions/deleteStakeholder";
// Actions
import findDocument from "./actions/findDocument";
import { addDocumentComment } from "../documentUpdate/actions/addDocumentComment";
import { findSigners } from "./actions/findSigners";
import { setDocumentPending } from "../documentUpdate/actions/setDocumentPending";
import { anullDocument } from "../documentUpdate/actions/anullDocument";
import { abandonDocument } from "../documentUpdate/actions/abandonDocument";
import { fetchDocumentHistory } from "./actions/fetchDocumentHistory";
import addSigner from "./actions/addSigner";
import { notifySignerByWhatsapp } from "./actions/notifySignerByWhatsapp";

interface DocumentShowState {
  document: Document;
  signers: DocumentStakeholder[];
  findStatus: string;
  segmentSelected: string;

  history: Message[];

  showUpdateDocumentModal: boolean;
  showAnullModal: boolean;
  showAbandonModal: boolean;
  showUpdateArchiveModal: boolean;
  showDocumentPendingModal: boolean;
  showDocumentCorrectionModal: boolean;
  searchText: string;
  userCount: number;
  notifyByWhatsappStatus: "idle" | "pending" | "succeeded" | "failed";
}

const initialState = {
  document: {},
  signers: [],
  findStatus: "",
  segmentSelected: "info", //"signers"
  signStatus: "",
  history: [],
  requestCorrectionStatus: "",
  showUpdateDocumentModal: false,
  showAnullModal: false,
  showAbandonModal: false,
  showUpdateArchiveModal: false,
  showDocumentPendingModal: false,
  showDocumentCorrectionModal: false,
  searchText: "",
  userCount: 0,
  notifyByWhatsappStatus: "idle",
} as DocumentShowState;

export const setSegmentSelected = createAction<string>("SET_SEGMENT_SELECTED_GESTION_DETAIL");
export const sortDocumentSigner = createAction<[...any]>("SORT_DOCUMENT_SIGNER");

export const setShowUpdateDocumentModal = createAction<boolean>("SET_SHOW_UPDATEDOCUMENT_MODAL");
export const setShowUpdateArchiveModal = createAction<boolean>("SET_SHOW_UPDATEARCHIVE_MODAL");
export const setShowAnullModal = createAction<boolean>("SET_SHOW_ANULL_MODAL");
export const setShowAbandonModal = createAction<boolean>("SET_SHOW_ABANDON_MODAL");
export const setShowDocumentPendingModal = createAction<boolean>("SET_SHOW_DOCUMENTPENDING_MODAL");
export const setShowDocumentCorrectionModal = createAction<boolean>(
  "SET_SHOW_DOCUMENTCORRECTION_MODAL"
);

export const setSearchText = createAction<string>("SET_SIGNERSEARCH_TEXT");
export const setUserCount = createAction<number>("SET_USER_COUNT");

const documentShowSlice = createSlice({
  name: "documentShow",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(setShowDocumentCorrectionModal, (state, { payload }) => {
        state.showDocumentCorrectionModal = payload;
      })
      .addCase(setShowUpdateDocumentModal, (state, { payload }) => {
        state.showUpdateDocumentModal = payload;
      })
      .addCase(setShowUpdateArchiveModal, (state, { payload }) => {
        state.showUpdateArchiveModal = payload;
      })
      .addCase(setShowAnullModal, (state, { payload }) => {
        state.showAnullModal = payload;
      })
      .addCase(setShowAbandonModal, (state, { payload }) => {
        state.showAbandonModal = payload;
      })
      .addCase(setShowDocumentPendingModal, (state, { payload }) => {
        state.showDocumentPendingModal = payload;
      })
      .addCase(sortDocumentSigner, (state, { payload }) => {
        state.signers = [...payload];
      })
      .addCase(setSegmentSelected, (state, { payload }) => {
        state.segmentSelected = payload;
      })

      .addCase(findDocument.pending, (state) => {
        state.findStatus = "pending";
        state.document = {} as Document;
        state.segmentSelected = "info";
        state.showUpdateDocumentModal = false;
        state.showAnullModal = false;
        state.showAbandonModal = false;
        state.showUpdateArchiveModal = false;
        state.showDocumentPendingModal = false;
        state.searchText = "";
      })
      .addCase(findDocument.fulfilled, (state, { payload }) => {
        state.document = { ...payload };
        state.findStatus = "fulfilled";
      })
      .addCase(findDocument.rejected, (state) => {
        state.findStatus = "rejected";
      })
      // Obtener firmantes
      .addCase(findSigners.pending, (state) => {
        state.signers = [];
      })
      .addCase(findSigners.fulfilled, (state, { payload }) => {
        state.signers = payload;
      })
      .addCase(findSigners.rejected, (state) => {})

      // Eliminar firmante
      .addCase(deleteStakeholder.pending, (state) => {})
      .addCase(deleteStakeholder.fulfilled, (state, { payload }) => {
        const { order } = payload;
        state.signers = state.signers
          .filter(({ id }) => id !== payload.id)
          .map((item) => {
            if (order && item.order) {
              return { ...item, order: item.order > order ? item.order - 1 : item.order };
            } else {
              return {} as DocumentStakeholder;
            }
          });
      })
      .addCase(deleteStakeholder.rejected, (state) => {})

      // Agregar firmante
      .addCase(addStakeholder.pending, (state) => {})
      .addCase(addStakeholder.fulfilled, (state, { payload }) => {
        state.signers.push(payload);
      })
      .addCase(addSigner.fulfilled, (state, { payload }) => {
        state.signers.push(payload);
      })
      .addCase(addStakeholder.rejected, (state) => {})

      .addCase(fetchDocumentHistory.pending, (state) => {
        state.findStatus = "pending";
      })
      .addCase(fetchDocumentHistory.fulfilled, (state, { payload }) => {
        state.history = [...payload];
        state.findStatus = "fulfilled";
      })
      .addCase(fetchDocumentHistory.rejected, (state) => {
        state.findStatus = "rejected";
      })
      .addCase(updateDocument.fulfilled, (state, { payload }) => {
        state.document = { ...state.document, ...payload };
      })
      .addCase(setDocumentPending.pending, (state) => {})
      .addCase(setDocumentPending.fulfilled, (state, { payload }) => {
        state.document = { ...state.document, ...payload };
      })

      .addCase(addDocumentHistory.fulfilled, (state, { payload }) => {
        state.history.unshift(payload);
      })
      .addCase(anullDocument.fulfilled, (state, { payload }) => {
        state.document = { ...state.document, ...payload };
      })
      .addCase(abandonDocument.fulfilled, (state, { payload }) => {
        state.document = { ...state.document, ...payload };
      })

      .addCase(moveStakeholderUp.fulfilled, (state, { payload }) => {
        state.signers = state.signers.map((signer) => {
          const exists = payload.find((newSigner) => newSigner.id === signer.id);
          return exists || signer;
        });
      })
      .addCase(moveStakeholderDown.fulfilled, (state, { payload }) => {
        state.signers = state.signers.map((signer) => {
          const exists = payload.find((newSigner) => newSigner.id === signer.id);
          return exists || signer;
        });
      })
      .addCase(setSearchText, (state, { payload }) => {
        state.searchText = payload;
      })
      .addCase(addDocumentComment.fulfilled, (state, { payload }) => {
        state.history.unshift(payload);
      })
      .addCase(setUserCount, (state, { payload }) => {
        state.userCount = payload;
      })
      .addCase(notifySignerByWhatsapp.pending, (state) => {
        state.notifyByWhatsappStatus = "pending";
      })
      .addCase(notifySignerByWhatsapp.fulfilled, (state) => {
        state.notifyByWhatsappStatus = "succeeded";
      })
      .addCase(notifySignerByWhatsapp.rejected, (state) => {
        state.notifyByWhatsappStatus = "failed";
      });
  },
});

export const documentShow = documentShowSlice.reducer;
